import React, { createContext, useState, useContext, useCallback, ReactNode } from 'react';
import ErrorModal from '../components/ErrorModal';
import WarningModal, { WarningItem } from '../components/WarningModal';

interface ErrorContextType {
  showError: (message: string) => void;
  showWarning: (title: string, message: string, items?: WarningItem[]) => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warningTitle, setWarningTitle] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [warningItems, setWarningItems] = useState<WarningItem[]>([]);

  const showError = useCallback((message: string) => {
    setErrorMessage(message);
    setIsErrorModalOpen(true);
  }, []);

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const showWarning = useCallback((title: string, message: string, items?: WarningItem[]) => {
    setWarningTitle(title);
    setWarningMessage(message);
    setWarningItems(items ?? []);
    setIsWarningModalOpen(true);
  }, []);

  const closeWarningModal = () => {
    setIsWarningModalOpen(false);
  };

  return (
    <ErrorContext.Provider value={{ showError, showWarning }}>
      {children}
      <ErrorModal 
        isOpen={isErrorModalOpen} 
        errorMessage={errorMessage} 
        onClose={closeErrorModal} 
      />
      <WarningModal
        isOpen={isWarningModalOpen}
        title={warningTitle}
        message={warningMessage}
        items={warningItems}
        onClose={closeWarningModal}
      />
    </ErrorContext.Provider>
  );
};

export default ErrorContext;